import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Check, Calendar, Clock, Users, Sparkles, CreditCard, User, Mail, Phone } from 'lucide-react';
import { playSoundIfEnabled } from '../utils/sounds';
import { workshops, mockBooking } from '../mock/mockData';
import { toast } from 'sonner';

const Booking = () => {
  const { workshopId } = useParams();
  const navigate = useNavigate();
  const workshop = workshops.find(w => w.id === workshopId);

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    parentName: '',
    parentEmail: '',
    parentPhone: '',
    childName: '',
    childAge: '',
    medicalInfo: '',
    emergencyContact: '',
    emergencyPhone: '',
    dietaryRequirements: '',
    agreeTerms: false
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!workshop) {
      navigate('/workshops');
    }
  }, [workshop, navigate]);

  if (!workshop) {
    return null;
  }

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.parentName.trim()) newErrors.parentName = 'Parent name is required';
    if (!formData.parentEmail.trim()) newErrors.parentEmail = 'Email is required';
    if (!/\S+@\S+\.\S+/.test(formData.parentEmail)) newErrors.parentEmail = 'Email is invalid';
    if (!formData.parentPhone.trim()) newErrors.parentPhone = 'Phone is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.childName.trim()) newErrors.childName = 'Child name is required';
    if (!formData.childAge) newErrors.childAge = 'Child age is required';
    if (!formData.emergencyContact.trim()) newErrors.emergencyContact = 'Emergency contact is required';
    if (!formData.emergencyPhone.trim()) newErrors.emergencyPhone = 'Emergency phone is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleNext = () => {
    playSoundIfEnabled('click');
    if (step === 1 && validateStep1()) {
      setStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (step === 2 && validateStep2()) {
      setStep(3);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    playSoundIfEnabled('click');
    setStep(prev => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agreeTerms) {
      toast.error('Please agree to terms and conditions');
      return;
    }

    playSoundIfEnabled('click');
    
    try {
      // Prepare booking data
      const bookingData = {
        workshop_id: workshop.id,
        workshop_title: workshop.title,
        workshop_date: workshop.dates,
        workshop_price: workshop.price,
        parent_name: formData.parentName,
        parent_email: formData.parentEmail,
        parent_phone: formData.parentPhone,
        child_name: formData.childName,
        child_age: parseInt(formData.childAge),
        special_requirements: [
          formData.medicalInfo && `Medical: ${formData.medicalInfo}`,
          formData.dietaryRequirements && `Dietary: ${formData.dietaryRequirements}`,
          formData.emergencyContact && `Emergency Contact: ${formData.emergencyContact} (${formData.emergencyPhone})`
        ].filter(Boolean).join(' | ') || null
      };
      
      // Send to backend
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData)
      });
      
      if (!response.ok) {
        throw new Error('Booking failed');
      }
      
      const result = await response.json();
      
      playSoundIfEnabled('success');
      toast.success('Booking request sent successfully!');
      
      // Store booking ID for confirmation page
      setFormData(prev => ({ ...prev, bookingId: result.id }));
      
      setStep(4);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (error) {
      playSoundIfEnabled('error');
      toast.error('Failed to submit booking. Please try again or contact us directly.');
    }
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
        {/* Back Button */}
        {step < 4 && (
          <button
            onClick={() => navigate('/workshops')}
            className="flex items-center gap-2 text-light-pink hover:text-white transition-colors mb-8 group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Back to Workshops
          </button>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Workshop Summary - Sidebar */}
          {step < 4 && (
            <div className="lg:col-span-1">
              <div className={`bg-${workshop.color} rounded-3xl p-6 sticky top-24`}>
                <h3 className="text-xl font-bold text-black mb-4">Workshop Summary</h3>
                
                <div className="mb-4">
                  <img 
                    src={workshop.image}
                    alt={workshop.title}
                    className="w-full h-48 object-cover rounded-2xl mb-4"
                  />
                </div>

                <h4 className="text-lg font-semibold text-black mb-2">{workshop.title}</h4>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2 text-black/80 text-sm">
                    <Users className="w-4 h-4" />
                    <span>{workshop.ageGroup}</span>
                  </div>
                  <div className="flex items-center gap-2 text-black/80 text-sm">
                    <Calendar className="w-4 h-4" />
                    <span>{workshop.dates}</span>
                  </div>
                  <div className="flex items-center gap-2 text-black/80 text-sm">
                    <Clock className="w-4 h-4" />
                    <span>{workshop.time}</span>
                  </div>
                </div>

                <div className="border-t border-black/10 pt-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-black/70">Workshop Fee</span>
                    <span className="text-black font-semibold">£{workshop.price}</span>
                  </div>
                  <div className="flex justify-between items-center text-xl font-bold text-black pt-2 border-t border-black/10">
                    <span>Total</span>
                    <span>£{workshop.price}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Booking Form */}
          <div className={step < 4 ? "lg:col-span-2" : "lg:col-span-3"}>
            {/* Progress Steps */}
            {step < 4 && (
              <div className="mb-8">
                <div className="flex items-center justify-between">
                  {[1, 2, 3].map((s) => (
                    <React.Fragment key={s}>
                      <div className="flex flex-col items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all duration-300 ${
                          step >= s ? 'bg-light-pink text-black' : 'bg-white/10 text-mid-grey'
                        }`}>
                          {step > s ? <Check className="w-5 h-5" /> : s}
                        </div>
                        <span className={`text-xs mt-2 ${step >= s ? 'text-light-pink' : 'text-mid-grey'}`}>
                          {s === 1 ? 'Parent Info' : s === 2 ? 'Child Info' : 'Review'}
                        </span>
                      </div>
                      {s < 3 && (
                        <div className={`flex-1 h-1 mx-2 rounded transition-all duration-300 ${
                          step > s ? 'bg-light-pink' : 'bg-white/10'
                        }`} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
              {/* Step 1: Parent Information */}
              {step === 1 && (
                <div>
                  <h2 className="text-3xl font-display font-bold text-white mb-2">Parent Information</h2>
                  <p className="text-mid-grey mb-8">Please provide your contact details</p>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-white mb-2 font-semibold">Full Name *</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-mid-grey" />
                        <input
                          type="text"
                          name="parentName"
                          value={formData.parentName}
                          onChange={handleInputChange}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-3 text-white focus:outline-none focus:border-light-pink transition-colors"
                          placeholder="Enter your full name"
                        />
                      </div>
                      {errors.parentName && <p className="text-red-400 text-sm mt-1">{errors.parentName}</p>}
                    </div>

                    <div>
                      <label className="block text-white mb-2 font-semibold">Email Address *</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-mid-grey" />
                        <input
                          type="email"
                          name="parentEmail"
                          value={formData.parentEmail}
                          onChange={handleInputChange}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-3 text-white focus:outline-none focus:border-light-pink transition-colors"
                          placeholder="your.email@example.com"
                        />
                      </div>
                      {errors.parentEmail && <p className="text-red-400 text-sm mt-1">{errors.parentEmail}</p>}
                    </div>

                    <div>
                      <label className="block text-white mb-2 font-semibold">Phone Number *</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-mid-grey" />
                        <input
                          type="tel"
                          name="parentPhone"
                          value={formData.parentPhone}
                          onChange={handleInputChange}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-3 text-white focus:outline-none focus:border-light-pink transition-colors"
                          placeholder="+44 1234 567890"
                        />
                      </div>
                      {errors.parentPhone && <p className="text-red-400 text-sm mt-1">{errors.parentPhone}</p>}
                    </div>

                    <button
                      onClick={handleNext}
                      className="w-full cta-button large bg-light-pink text-black hover:bg-mid-yellow transition-all duration-300"
                    >
                      Continue to Child Information
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Child Information */}
              {step === 2 && (
                <div>
                  <h2 className="text-3xl font-display font-bold text-white mb-2">Child Information</h2>
                  <p className="text-mid-grey mb-8">Tell us about your young actor</p>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-white mb-2 font-semibold">Child's Full Name *</label>
                      <input
                        type="text"
                        name="childName"
                        value={formData.childName}
                        onChange={handleInputChange}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-light-pink transition-colors"
                        placeholder="Enter child's full name"
                      />
                      {errors.childName && <p className="text-red-400 text-sm mt-1">{errors.childName}</p>}
                    </div>

                    <div>
                      <label className="block text-white mb-2 font-semibold">Child's Age *</label>
                      <input
                        type="number"
                        name="childAge"
                        value={formData.childAge}
                        onChange={handleInputChange}
                        min="5"
                        max="15"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-light-pink transition-colors"
                        placeholder="Enter child's age"
                      />
                      {errors.childAge && <p className="text-red-400 text-sm mt-1">{errors.childAge}</p>}
                    </div>

                    <div>
                      <label className="block text-white mb-2 font-semibold">Emergency Contact Name *</label>
                      <input
                        type="text"
                        name="emergencyContact"
                        value={formData.emergencyContact}
                        onChange={handleInputChange}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-light-pink transition-colors"
                        placeholder="Alternative emergency contact"
                      />
                      {errors.emergencyContact && <p className="text-red-400 text-sm mt-1">{errors.emergencyContact}</p>}
                    </div>

                    <div>
                      <label className="block text-white mb-2 font-semibold">Emergency Contact Phone *</label>
                      <input
                        type="tel"
                        name="emergencyPhone"
                        value={formData.emergencyPhone}
                        onChange={handleInputChange}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-light-pink transition-colors"
                        placeholder="+44 1234 567890"
                      />
                      {errors.emergencyPhone && <p className="text-red-400 text-sm mt-1">{errors.emergencyPhone}</p>}
                    </div>

                    <div>
                      <label className="block text-white mb-2 font-semibold">Medical Information</label>
                      <textarea
                        name="medicalInfo"
                        value={formData.medicalInfo}
                        onChange={handleInputChange}
                        rows="3"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-light-pink transition-colors resize-none"
                        placeholder="Any medical conditions, allergies, or important information we should know..."
                      />
                    </div>

                    <div>
                      <label className="block text-white mb-2 font-semibold">Dietary Requirements</label>
                      <textarea
                        name="dietaryRequirements"
                        value={formData.dietaryRequirements}
                        onChange={handleInputChange}
                        rows="2"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-light-pink transition-colors resize-none"
                        placeholder="Any dietary requirements or allergies..."
                      />
                    </div>

                    <div className="flex gap-4">
                      <button
                        onClick={handleBack}
                        className="flex-1 cta-button large bg-white/10 text-white hover:bg-white/20 transition-all duration-300"
                      >
                        Back
                      </button>
                      <button
                        onClick={handleNext}
                        className="flex-1 cta-button large bg-light-pink text-black hover:bg-mid-yellow transition-all duration-300"
                      >
                        Review Booking
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Review & Confirm */}
              {step === 3 && (
                <div>
                  <h2 className="text-3xl font-display font-bold text-white mb-2">Review & Confirm</h2>
                  <p className="text-mid-grey mb-8">Please review your booking details</p>

                  <div className="space-y-6">
                    {/* Parent Info */}
                    <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                      <h3 className="text-light-pink font-semibold mb-4 flex items-center gap-2">
                        <User className="w-5 h-5" />
                        Parent Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-mid-grey">Name:</span>
                          <p className="text-white">{formData.parentName}</p>
                        </div>
                        <div>
                          <span className="text-mid-grey">Email:</span>
                          <p className="text-white">{formData.parentEmail}</p>
                        </div>
                        <div>
                          <span className="text-mid-grey">Phone:</span>
                          <p className="text-white">{formData.parentPhone}</p>
                        </div>
                      </div>
                    </div>

                    {/* Child Info */}
                    <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                      <h3 className="text-mid-purple font-semibold mb-4 flex items-center gap-2">
                        <Sparkles className="w-5 h-5" />
                        Child Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-mid-grey">Name:</span>
                          <p className="text-white">{formData.childName}</p>
                        </div>
                        <div>
                          <span className="text-mid-grey">Age:</span>
                          <p className="text-white">{formData.childAge} years</p>
                        </div>
                        <div>
                          <span className="text-mid-grey">Emergency Contact:</span>
                          <p className="text-white">{formData.emergencyContact}</p>
                        </div>
                        <div>
                          <span className="text-mid-grey">Emergency Phone:</span>
                          <p className="text-white">{formData.emergencyPhone}</p>
                        </div>
                        {formData.medicalInfo && (
                          <div className="md:col-span-2">
                            <span className="text-mid-grey">Medical Info:</span>
                            <p className="text-white">{formData.medicalInfo}</p>
                          </div>
                        )}
                        {formData.dietaryRequirements && (
                          <div className="md:col-span-2">
                            <span className="text-mid-grey">Dietary Requirements:</span>
                            <p className="text-white">{formData.dietaryRequirements}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Terms */}
                    <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          name="agreeTerms"
                          checked={formData.agreeTerms}
                          onChange={handleInputChange}
                          className="mt-1 w-5 h-5 rounded border-white/20 text-light-pink focus:ring-light-pink"
                        />
                        <span className="text-white text-sm">
                          I agree to the terms and conditions and understand that my booking will be 
                          confirmed once payment is received. I will receive payment details via email.
                        </span>
                      </label>
                    </div>

                    {/* Payment Info */}
                    <div className="bg-gradient-to-r from-light-pink/10 to-mid-purple/10 rounded-xl p-6 border border-light-pink/20">
                      <div className="flex items-center gap-3 mb-3">
                        <CreditCard className="w-6 h-6 text-light-pink" />
                        <h3 className="text-white font-semibold">Payment Information</h3>
                      </div>
                      <p className="text-mid-grey text-sm mb-2">
                        After submitting your booking, we'll send you payment details via email. 
                        Payment can be made via Revolut or bank transfer.
                      </p>
                      <p className="text-light-pink text-sm">
                        Workshop Price: £{workshop.price}
                      </p>
                    </div>

                    <div className="flex gap-4">
                      <button
                        onClick={handleBack}
                        className="flex-1 cta-button large bg-white/10 text-white hover:bg-white/20 transition-all duration-300"
                      >
                        Back
                      </button>
                      <button
                        onClick={handleSubmit}
                        disabled={!formData.agreeTerms}
                        className="flex-1 cta-button large bg-light-pink text-black hover:bg-mid-yellow transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Confirm Booking
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Confirmation */}
              {step === 4 && (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-gradient-to-br from-light-pink to-mid-purple rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                    <Check className="w-10 h-10 text-white" />
                  </div>
                  
                  <h2 className="text-4xl font-display font-bold text-white mb-4">
                    Booking Request Received!
                  </h2>
                  
                  <p className="text-xl text-mid-grey mb-8 max-w-2xl mx-auto">
                    Thank you for your booking request! We've received your details and will contact you 
                    shortly via email or phone to confirm your booking and arrange payment.
                  </p>

                  <div className="bg-white/5 rounded-2xl p-8 border border-white/10 mb-8 max-w-2xl mx-auto">
                    <h3 className="text-light-pink font-semibold mb-4">Booking Reference</h3>
                    <p className="text-3xl font-mono font-bold text-white mb-6">
                      {formData.bookingId || 'Processing...'}
                    </p>
                    
                    <div className="space-y-3 text-left">
                      <div className="flex justify-between text-sm">
                        <span className="text-mid-grey">Workshop:</span>
                        <span className="text-white font-semibold">{workshop.title}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-mid-grey">Participant:</span>
                        <span className="text-white">{formData.childName}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-mid-grey">Dates:</span>
                        <span className="text-white">{workshop.dates}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-mid-grey">Contact Email:</span>
                        <span className="text-white">{formData.parentEmail}</span>
                      </div>
                      <div className="flex justify-between text-sm pt-3 border-t border-white/10">
                        <span className="text-mid-grey">Workshop Price:</span>
                        <span className="text-light-pink font-bold text-lg">£{workshop.price}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-light-pink/10 to-mid-purple/10 rounded-xl p-6 border border-light-pink/20 mb-8 max-w-2xl mx-auto">
                    <h4 className="text-white font-semibold mb-2">📧 What Happens Next?</h4>
                    <ul className="text-mid-grey text-sm text-left space-y-2">
                      <li>✓ We'll review your booking request</li>
                      <li>✓ You'll receive a confirmation email within 24 hours</li>
                      <li>✓ We'll send you payment details (Revolut or bank transfer)</li>
                      <li>✓ Your spot will be confirmed once payment is received</li>
                    </ul>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link
                      to="/workshops"
                      className="cta-button large bg-light-pink text-black hover:bg-mid-yellow transition-all duration-300"
                      onClick={() => playSoundIfEnabled('click')}
                    >
                      Book Another Workshop
                    </Link>
                    <Link
                      to="/"
                      className="cta-button large bg-white/10 text-white hover:bg-white/20 transition-all duration-300"
                      onClick={() => playSoundIfEnabled('click')}
                    >
                      Return Home
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Booking;
